import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';

// State
import { setCheckoutPayType } from '../../../state/modules/order/actions';
import useCheckoutPricing from '../useCheckoutPricing';

// Config
import config from '../../../config';

export default function PayTypeSwitcher() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { isContactFreeDelivery, deliveryType, payType, isCheckoutLocalStorageDone } = useSelector(
    state => state.order.checkout,
  );
  const pay_card_to_courier = useSelector(
    state => state.order.checkout.currentRestaurant?.pay_card_to_courier,
  );
  const pay_card_takeaway = useSelector(
    state => state.order.checkout.currentRestaurant?.pay_card_takeaway,
  );
  const onlinePaySystem = useSelector(
    state => state.order.checkout.currentRestaurant?.pay_params?.online_pay_system,
  );
  const googlePayIsAvailable = useSelector(state => state.payTypes.googlePay.isAvailable);
  const applePay = useSelector(state => state.payTypes.applePay);
  const apple_pay_extended = useSelector(state => state.city.userCity.apple_pay_extended);
  const { priceIncludingBonuses } = useCheckoutPricing();

  const cardToCourierDisabled = useMemo(() => {
    return (
      priceIncludingBonuses === 0 ||
      deliveryType !== 'delivery' ||
      isContactFreeDelivery ||
      !pay_card_to_courier
    );
  }, [priceIncludingBonuses, deliveryType, isContactFreeDelivery, pay_card_to_courier]);

  const onlineDisabled = useMemo(() => {
    return priceIncludingBonuses === 0 || (onlinePaySystem !== 4 && onlinePaySystem !== 5);
  }, [priceIncludingBonuses, onlinePaySystem]);
  const cardTakeawayDisabled = useMemo(() => {
    return priceIncludingBonuses === 0 || deliveryType === 'delivery' || !pay_card_takeaway;
  }, [priceIncludingBonuses, deliveryType, pay_card_takeaway]);
  const applePayDisabled = useMemo(() => {
    return priceIncludingBonuses === 0 || !applePay || !apple_pay_extended;
  }, [priceIncludingBonuses, applePay, apple_pay_extended]);

  const googlePayDisabled = useMemo(() => {
    return priceIncludingBonuses === 0 || !googlePayIsAvailable;
  }, [priceIncludingBonuses, googlePayIsAvailable]);

  // Оплата: наличными, картой курьеру, картой на сайте
  const payTypes = useMemo(() => {
    return [
      config.legal !== 'kg' && {
        value: 'online',
        label: intl.formatMessage({ id: 'order.payment.online' }),
        disabled: onlineDisabled,
      },
      {
        value: 'card_to_courier',
        label: intl.formatMessage({ id: 'order.payment.card_to_courier' }),
        disabled: cardToCourierDisabled,
      },
      {
        value: 'card_takeaway',
        label: intl.formatMessage({ id: 'order.payment.card_takeaway' }),
        disabled: cardTakeawayDisabled,
      },
      {
        value: 'cash',
        label: intl.formatMessage({ id: 'order.payment.cash' }),
        disabled: isContactFreeDelivery,
      },
      config.legal !== 'kg' && {
        value: 'applePay',
        label: 'Apple Pay',
        disabled: applePayDisabled,
      },
      config.legal !== 'kg' && {
        value: 'googlePay',
        label: 'Google Pay',
        disabled: googlePayDisabled,
      },
    ].filter(Boolean);
  }, [
    isContactFreeDelivery,
    cardToCourierDisabled,
    onlineDisabled,
    applePayDisabled,
    googlePayDisabled,
  ]);

  useEffect(() => {
    const isCashDisabled = payType === 'cash' && isContactFreeDelivery;
    const isCardToCourierDisabled = payType === 'card_to_courier' && cardToCourierDisabled;
    const isOnlineDisabled = payType === 'online' && onlineDisabled;
    const isApplePayDisabled = payType === 'applePay' && applePayDisabled;
    const isGooglePayDisabled = payType === 'googlePay' && googlePayDisabled;

    if (
      isCheckoutLocalStorageDone &&
      (isCashDisabled ||
        isCardToCourierDisabled ||
        isOnlineDisabled ||
        isApplePayDisabled ||
        isGooglePayDisabled ||
        payType === null)
    ) {
      const newPayType = payTypes.find(pt => !pt.disabled)?.value || null;

      dispatch(setCheckoutPayType(newPayType));
    }
  }, [
    isContactFreeDelivery,
    cardToCourierDisabled,
    onlineDisabled,
    applePayDisabled,
    googlePayDisabled,
    isCheckoutLocalStorageDone,
  ]);

  return { payTypes };
}
